"use client"

import { ComponentConfig } from "@measured/puck"
import { useEffect, useState } from "react"
import baseService from "../../../../../base-system/service/base"
import { configs, getSettings } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { DataSettingItem } from "../../../types"

export interface FooterProps {
  img?: string
  title?: string
  baseAddress?: string
  phoneNumber?: string
  location?: string
}

// Cấu trúc nhóm menu footer (khớp type Menu bên ske-next-app).
interface Menu {
  id: number
  title: string
  url: string
  attribute?: Menu[]
}

interface BaseSystem {
  id: string
  attributes: {
    name: string
    email: string
    workingTime: string
    address: string
    hotline: string
    latitude: string
    longitude: string
    linkMap: string
  }
}

export interface SKEFooterProps extends CommonStylesProps {
  items: FooterProps[]
  img?: string
  title?: string
  intro?: string
  ctaText?: string
  ctaLink?: string
  campusAllLink?: string
}

// Nhóm nav mặc định. Trong CMS (preview) dùng menu tĩnh này; site thật (ske-next-app)
// sẽ thay bằng menu động từ Strapi qua getFooterMenu.
const DEFAULT_FOOTER_MENU: Menu[] = [
  {
    id: -1,
    title: "Về Sky-Line",
    url: "/tong-quan-ve-he-thong",
    attribute: [
      { id: -11, title: "Giới thiệu hệ thống", url: "/tong-quan-ve-he-thong" },
      { id: -12, title: "Hệ thống các cơ sở", url: "/he-thong-cac-co-so" },
      { id: -13, title: "Thành tựu Sky-Line", url: "/thanh-tuu-sky-line" },
      { id: -14, title: "Tin tức – Hoạt động", url: "/hoat-dong-su-kien" },
      { id: -15, title: "Liên hệ", url: "/lien-he" },
    ],
  },
  {
    id: -2,
    title: "Chương trình học",
    url: "/chuong-trinh-hoc/bac-mam-non",
    attribute: [
      { id: -21, title: "Mầm non", url: "/chuong-trinh-hoc/bac-mam-non" },
      { id: -22, title: "Tiểu học", url: "/chuong-trinh-hoc/bac-tieu-hoc" },
      { id: -23, title: "Trung học cơ sở", url: "/chuong-trinh-hoc/bac-trung-hoc-co-so" },
      { id: -24, title: "Trung học phổ thông", url: "/chuong-trinh-hoc/bac-trung-hoc-pho-thong" },
    ],
  },
  {
    id: -3,
    title: "Tuyển sinh",
    url: "/thong-tin-tuyen-sinh",
    attribute: [
      { id: -31, title: "Thông tin tuyển sinh", url: "/thong-tin-tuyen-sinh" },
      { id: -32, title: "Quy trình tuyển sinh", url: "/quy-trinh-tuyen-sinh" },
      { id: -33, title: "Học phí", url: "/hoc-phi" },
      { id: -34, title: "Câu hỏi thường gặp", url: "/cac-cau-hoi-thuong-gap" },
    ],
  },
  {
    id: -4,
    title: "Góc phụ huynh",
    url: "/thong-bao-moi",
    attribute: [
      { id: -41, title: "Thông báo mới", url: "/thong-bao-moi" },
      { id: -42, title: "Thực đơn học sinh", url: "/thuc-don-hoc-sinh" },
      { id: -43, title: "Sổ tay phụ huynh học sinh", url: "/so-tay-phu-huynh-hoc-sinh" },
      {
        id: -44,
        title: "Thư viện",
        url: "",
        attribute: [
          { id: -441, title: "Thư viện ảnh", url: "/thu-vien-anh" },
          { id: -442, title: "Thư viện video", url: "/thu-vien-video" },
        ],
      },
    ],
  },
]

// Fallback dùng khi dữ liệu Puck đã lưu trong CMS chưa có các field mới.
const DEFAULT_INTRO =
  "Hệ thống giáo dục liên cấp từ Mầm non đến THPT tại Đà Nẵng và Quảng Nam, với triết lý “Học để sống hạnh phúc”."
const DEFAULT_CTA_TEXT = "Đăng ký tư vấn"
const DEFAULT_CTA_LINK = "https://tuyensinh.skylineschool.edu.vn/"

const ChevronIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="M5 7.5 10 12.5 15 7.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Social icon đơn sắc (fill=currentColor) — sạch trên nền tối, đổi màu khi hover.
const socialSvgClass = "h-[18px] w-[18px] fill-current"

const IconFacebook = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={socialSvgClass}>
    <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6c-.3 0-1.3-.1-2.45-.1-2.4 0-4.05 1.5-4.05 4.2v2.2H7.5V13h2.7v8h3.3z" />
  </svg>
)
const IconYoutube = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={socialSvgClass}>
    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.25 5 12 5 12 5s-6.25 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.75 19 12 19 12 19s6.25 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15V9l5.2 3-5.2 3z" />
  </svg>
)
const IconTiktok = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={socialSvgClass}>
    <path d="M16.5 3c.35 1.9 1.5 3.3 3.5 3.6v2.6c-1.35.05-2.6-.35-3.75-1.05v4.9c0 4.15-3.4 6.6-6.65 5.5-2.35-.8-3.6-3.1-3.35-5.35.3-2.55 2.6-4.4 5.3-4.1v2.7c-.45-.1-.95-.1-1.4.05-1.15.35-1.8 1.45-1.5 2.6.3 1.15 1.5 1.85 2.7 1.5 1.05-.3 1.75-1.25 1.75-2.4V3h3.4z" />
  </svg>
)
// Zalo — phương án "khoét ngược" (giữ nguyên hình học logo, đảo sáng-tối thành
// line-art): khung squircle + chữ "Zalo" là currentColor, bong bóng thành lỗ.
// Nhẹ và khớp nét mảnh của 3 icon còn lại.
const IconZalo = () => (
  <svg viewBox="0 0 50 50" aria-hidden="true" className="h-[22px] w-[22px]">
    <mask id="ske-zalo" maskUnits="userSpaceOnUse" x="0" y="0" width="50" height="50">
      <path
        fill="#fff"
        d="M22.782 0.166016H27.199C33.2653 0.166016 36.8103 1.05701 39.9572 2.74421C43.1041 4.4314 45.5875 6.89585 47.2557 10.0428C48.9429 13.1897 49.8339 16.7347 49.8339 22.801V27.1991C49.8339 33.2654 48.9429 36.8104 47.2557 39.9573C45.5685 43.1042 43.1041 45.5877 39.9572 47.2559C36.8103 48.9431 33.2653 49.8341 27.199 49.8341H22.8009C16.7346 49.8341 13.1896 48.9431 10.0427 47.2559C6.89583 45.5687 4.41243 43.1042 2.7442 39.9573C1.057 36.8104 0.166016 33.2654 0.166016 27.1991V22.801C0.166016 16.7347 1.057 13.1897 2.7442 10.0428C4.43139 6.89585 6.89583 4.41245 10.0427 2.74421C13.1707 1.05701 16.7346 0.166016 22.782 0.166016Z"
      />
      <path
        fill="#000"
        d="M7.779 43.5892C10.1019 43.846 13.0061 43.1836 15.0682 42.1825C24.0225 47.1318 38.0197 46.8954 46.4923 41.4732C46.8209 40.9803 47.1279 40.4677 47.4128 39.9363C49.1062 36.7779 50.0004 33.22 50.0004 27.1316V22.7175C50.0004 16.629 49.1062 13.0711 47.4128 9.91273C45.7385 6.75436 43.2461 4.28093 40.0877 2.58758C36.9293 0.894239 33.3714 0 27.283 0H22.8499C17.6644 0 14.2982 0.652754 11.4699 1.89893C11.3153 2.03737 11.1636 2.17818 11.0151 2.32135C2.71734 10.3203 2.08658 27.6593 9.12279 37.0782C9.13064 37.0921 9.13933 37.1061 9.14889 37.1203C10.2334 38.7185 9.18694 41.5154 7.55068 43.1516C7.28431 43.399 7.37944 43.5512 7.779 43.5892Z"
      />
      <path
        fill="#fff"
        d="M20.5632 17H10.8382V19.0853H17.5869L10.9329 27.3317C10.7244 27.635 10.5728 27.9194 10.5728 28.5639V29.0947H19.748C20.203 29.0947 20.5822 28.7156 20.5822 28.2606V27.1421H13.4922L19.748 19.2938C19.8428 19.1801 20.0134 18.9716 20.0893 18.8768L20.1272 18.8199C20.4874 18.2891 20.5632 17.8341 20.5632 17.2844V17Z"
      />
      <path
        fill="#fff"
        d="M25.814 19.6924C23.1979 19.6924 21.0747 21.8156 21.0747 24.4317C21.0747 27.0478 23.1979 29.171 25.814 29.171C28.4301 29.171 30.5533 27.0478 30.5533 24.4317C30.5723 21.8156 28.4491 19.6924 25.814 19.6924ZM25.814 27.2184C24.2785 27.2184 23.0273 25.9672 23.0273 24.4317C23.0273 22.8962 24.2785 21.645 25.814 21.645C27.3495 21.645 28.6007 22.8962 28.6007 24.4317C28.6007 25.9672 27.3685 27.2184 25.814 27.2184Z"
      />
      <path
        fill="#fff"
        d="M29.4562 29.0944H30.5747V19.957H28.6221V28.2793C28.6221 28.7153 29.0012 29.0944 29.4562 29.0944Z"
      />
      <path
        fill="#fff"
        d="M32.9416 29.0947H34.3255V17H32.2402V28.3933C32.2402 28.7725 32.5435 29.0947 32.9416 29.0947Z"
      />
      <path
        fill="#fff"
        d="M40.4867 19.6162C37.8516 19.6162 35.7095 21.7584 35.7095 24.3934C35.7095 27.0285 37.8516 29.1707 40.4867 29.1707C43.1217 29.1707 45.2639 27.0285 45.2639 24.3934C45.2639 21.7584 43.1217 19.6162 40.4867 19.6162ZM40.4867 27.2181C38.9322 27.2181 37.681 25.9669 37.681 24.4124C37.681 22.8579 38.9322 21.6067 40.4867 21.6067C42.0412 21.6067 43.2924 22.8579 43.2924 24.4124C43.2924 25.9669 42.0412 27.2181 40.4867 27.2181Z"
      />
    </mask>
    <rect width="50" height="50" fill="currentColor" mask="url(#ske-zalo)" />
  </svg>
)

export const RenderConfig: ComponentConfig<SKEFooterProps> = {
  render: ({ img, title, intro, ctaText, ctaLink, campusAllLink }) => {
    const introText = intro || DEFAULT_INTRO
    const ctaLabel = ctaText || DEFAULT_CTA_TEXT
    const ctaHref = ctaLink || DEFAULT_CTA_LINK
    const [settingsData, setSettingsData] = useState<DataSettingItem[]>([])
    const [baseSystems, setBaseSystems] = useState<BaseSystem[]>([])
    const [menuGroups] = useState<Menu[]>(DEFAULT_FOOTER_MENU)
    const [openGroups, setOpenGroups] = useState<{ [key: number]: boolean }>({})

    const { fetchAllBases } = baseService()

    const getSetting = (key: string) => settingsData.find(item => item.attributes.key === key)?.attributes.value

    const fetchSetting = async () => {
      try {
        const response = await getSettings()
        setSettingsData(response)
      } catch (error) {
        console.error("Error fetching setting data:", error)
      }
    }

    const fetchBaseSystems = async () => {
      try {
        const response = await fetchAllBases()
        setBaseSystems(response?.data || [])
      } catch (error) {
        console.error("Error fetching base systems:", error)
      }
    }

    useEffect(() => {
      fetchSetting()
      fetchBaseSystems()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const toggleGroup = (id: number) => {
      setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }))
    }

    const socials = [
      { key: "facebookLink", Icon: IconFacebook, label: "Facebook" },
      { key: "youtubeLink", Icon: IconYoutube, label: "YouTube" },
      { key: "tiktokLink", Icon: IconTiktok, label: "TikTok" },
      { key: "zaloLink", Icon: IconZalo, label: "Zalo" },
    ]

    const email = getSetting("contactEmail")

    return (
      <footer className="bg-[#0b1c33] text-white">
        <div className="max-w-[1280px] mx-auto px-4 pt-14 sm:px-6 sm:pt-16">
          {/* ===== A. THƯƠNG HIỆU + ĐIỀU HƯỚNG ===== */}
          <div className="grid gap-10 lg:grid-cols-[1.15fr_3fr] lg:gap-14 md:grid-cols-2">
            {/* Brand */}
            <div>
              <a
                href="/"
                aria-label="Hệ thống Giáo dục Sky-Line — Trang chủ"
                className="inline-flex items-center gap-2"
              >
                {img ? (
                  <img alt="Sky-Line" src={`${configs.API_URL}${img}`} className="w-[165px] h-[59px] object-contain" />
                ) : (
                  <span className="text-[26px] font-extrabold tracking-[0.06em] text-[#12b5a5]">
                    SKY<span className="text-white font-light">-</span>LINE
                  </span>
                )}
              </a>

              {introText && <p className="mt-4 max-w-[34ch] text-sm leading-7 text-white/70">{introText}</p>}

              {email && (
                <a href={`mailto:${email}`} className="mt-3.5 inline-block text-sm text-white/70 hover:text-[#12b5a5]">
                  {email}
                </a>
              )}

              <ul className="mt-4 flex list-none gap-2.5 p-0">
                {socials.map(({ key, Icon, label }) => {
                  const href = getSetting(key)
                  if (!href) return null
                  return (
                    <li key={key}>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-[#12b5a5] hover:text-[#0b1c33]"
                      >
                        <Icon />
                      </a>
                    </li>
                  )
                })}
              </ul>

              {ctaHref && (
                <a
                  href={ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-[22px] inline-flex min-h-[46px] items-center justify-center rounded-full bg-[#12b5a5] px-[26px] text-sm font-bold text-[#0b1c33] transition-opacity hover:opacity-90"
                >
                  {ctaLabel}
                </a>
              )}
            </div>

            {/* Nav */}
            <nav
              aria-label="Điều hướng cuối trang"
              className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8 lg:grid-cols-4 lg:gap-x-7"
            >
              {menuGroups.map(group => {
                const isOpen = !!openGroups[group.id]
                return (
                  <div key={group.id} className="border-b border-white/[0.09] md:border-b-0">
                    <h3 className="m-0">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        aria-expanded={isOpen}
                        className="flex w-full items-center justify-between gap-3 min-h-[48px] cursor-pointer text-[13px] font-bold uppercase tracking-[0.08em] text-white md:min-h-0 md:pb-3 md:cursor-default md:pointer-events-none"
                      >
                        {group.title}
                        <ChevronIcon
                          className={`h-[18px] w-[18px] flex-none transition-transform md:hidden ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </h3>
                    <div className={`${isOpen ? "block" : "hidden"} pb-2.5 md:block md:pb-0`}>
                      <ul className="list-none m-0 p-0">
                        {group.attribute?.map(child =>
                          child.attribute && child.attribute.length > 0 ? (
                            // Cấp 3: subtitle (vd "Thư viện") + link con
                            <li key={child.id}>
                              <p className="mt-4 mb-0.5 text-[11px] font-bold uppercase tracking-[0.1em] text-white/55">
                                {child.title}
                              </p>
                              <ul className="list-none m-0 p-0">
                                {child.attribute.map(sub => (
                                  <li key={sub.id}>
                                    <a
                                      href={sub.url}
                                      className="inline-block py-[9px] text-sm leading-5 text-white/70 transition-colors hover:text-[#12b5a5] hover:underline underline-offset-4 md:py-[5px]"
                                    >
                                      {sub.title}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ) : (
                            <li key={child.id}>
                              <a
                                href={child.url}
                                className="inline-block py-[9px] text-sm leading-5 text-white/70 transition-colors hover:text-[#12b5a5] hover:underline underline-offset-4 md:py-[5px]"
                              >
                                {child.title}
                              </a>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                )
              })}
            </nav>
          </div>

          {/* ===== B. HỆ THỐNG CƠ SỞ ===== */}
          <section aria-labelledby="campus-heading" className="mt-11 border-t border-white/[0.14] pt-8">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 id="campus-heading" className="m-0 text-[13px] font-bold uppercase tracking-[0.08em] text-white">
                {title || "Hệ thống cơ sở"}
              </h2>
              <a
                href={campusAllLink || "/he-thong-cac-co-so"}
                className="text-sm text-[#12b5a5] hover:underline underline-offset-4"
              >
                Xem tất cả cơ sở
              </a>
            </div>

            <ul className="list-none mt-5 p-0 grid grid-cols-1 gap-x-9 gap-y-[22px] md:grid-cols-2 lg:grid-cols-3">
              {baseSystems.map(base => (
                <li key={base.id} className="border-t border-white/[0.09] pt-3.5">
                  <p className="m-0 text-sm font-bold text-white">{base.attributes.name}</p>
                  <p className="mt-1.5 mb-0 text-[13px] leading-[1.65] text-white/70 tracking-tight">
                    {base.attributes.address}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1.5">
                    {base.attributes.hotline && (
                      <a
                        href={`tel:${base.attributes.hotline.replace(/[^\d+]/g, "")}`}
                        className="inline-block py-1.5 text-[13px] text-white/70 hover:text-[#12b5a5] md:py-0.5"
                      >
                        {base.attributes.hotline}
                      </a>
                    )}
                    {base.attributes.linkMap && (
                      <a
                        href={base.attributes.linkMap}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block py-1.5 text-[13px] text-[#12b5a5] hover:underline underline-offset-4 md:py-0.5"
                      >
                        Xem bản đồ
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* ===== C. THANH CUỐI ===== */}
        <div className="mt-11 border-t border-white/[0.14] bg-[#081527]">
          <div className="max-w-[1280px] mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-[18px] sm:px-6">
            <p className="m-0 text-[13px] text-white/55">© {new Date().getFullYear()} Hệ thống Giáo dục Sky-Line</p>
            <a href="/lien-he" className="text-[13px] text-white/70 hover:text-[#12b5a5]">
              Liên hệ
            </a>
          </div>
        </div>
      </footer>
    )
  },
}
